import type { ContactCardDto, LinkPreviewDto, LocationPoint, MessageDto } from '@dijitalapp/shared';
import type { MessageKey } from '@dijitalapp/i18n';
import { ApiError, type ClientErrorCode } from './api';
import type { DecryptedContent, EncryptedEvent, EncryptedPoll } from './e2ee';
import { readLinkCard, type EncryptedLinkCard } from './linkCard';

/*
 * Forwarding, like WhatsApp and Signal. The server can copy a message only when it can read it and the target
 * accepts plaintext: plaintext → plaintext goes to POST …/forward (attachments duplicated server-side). Whenever
 * the source or the target is end-to-end encrypted the device does it: it reads the content (decrypting it
 * if needed) and sends it again to each target through the ordinary send paths, which encrypt per target. The
 * copy is marked forwarded and never names the original sender. View-once media is never forwarded.
 */

// Same names as in e2ee.ts (not imported from there: this module stays free of the crypto library).
const CONTACT = 'app.dijitalapp.contact';
const POLL = 'app.dijitalapp.poll';
const EVENT = 'app.dijitalapp.event';

/** Where a copy comes from: a file the server can read, or ciphertext with its key (from the decrypted message). */
export type ForwardFileSource = { encrypted: false; attachmentId: string } | { encrypted: true; attachmentId: string; info: Record<string, unknown> };

export interface ForwardFileMeta {
  name: string;
  mime: string;
  size: number;
  voice: boolean;
  durationMs?: number;
  waveform?: number[];
  round?: boolean;
  /** A photo sent as it is (shown as a file). */
  document?: boolean;
  /** A sticker someone made (a picture shown without a bubble). */
  sticker?: boolean;
  /** Encrypted sources: the cover / tiny stand-in and the shape, carried over as they were. */
  thumb?: string;
  width?: number;
  height?: number;
}

/** What a message is, as far as forwarding goes. */
export type ForwardItem =
  | { kind: 'text'; body: string; linkCard: EncryptedLinkCard | null; linkPreview: LinkPreviewDto | null }
  | { kind: 'file'; source: ForwardFileSource; meta: ForwardFileMeta; caption: string }
  | { kind: 'sticker'; sticker: string }
  | { kind: 'location'; point: LocationPoint }
  | { kind: 'contact'; card: ContactCardDto }
  | { kind: 'poll'; poll: EncryptedPoll }
  | { kind: 'event'; event: EncryptedEvent };

export type ForwardRoute = 'server' | 'device' | 'never';

/** Why a copy did not go: an API error code, or the device could not read / cannot forward the message. */
export type ForwardFailure = ClientErrorCode | 'UNREADABLE' | 'NOT_FORWARDABLE';
/** How one copy ended: sent, waiting for the network (it goes by itself later), or refused and why. */
export type SendSettled = { ok: true; queued?: boolean } | { ok: false; code: ForwardFailure };
export type ForwardOutcome = SendSettled & { conversationId: string };

/** Which path one copy takes. */
export function forwardRoute(source: Pick<MessageDto, 'contentType' | 'viewOnce' | 'deleted'>, target: { e2ee: boolean }): ForwardRoute {
  if (source.viewOnce || source.deleted || source.contentType === 'system') return 'never';
  return source.contentType === 'encrypted' || target.e2ee ? 'device' : 'server';
}

const text = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined);

/** "geo:lat,lng;u=accuracy" (the Matrix m.location form used inside encrypted messages). */
export function geoPoint(uri: string | undefined): LocationPoint | null {
  const m = /^geo:(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)(?:;u=(\d+(?:\.\d+)?))?/.exec(uri ?? '');
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng, accuracy: m[3] ? Number(m[3]) : null };
}

const isCard = (c: unknown): c is ContactCardDto =>
  !!c && typeof c === 'object' && /^[0-9a-f-]{36}$/i.test(String((c as { id?: unknown }).id)) && typeof (c as { username?: unknown }).username === 'string' && typeof (c as { displayName?: unknown }).displayName === 'string';

/** A plaintext message (what the server sent us). */
function plainItem(m: MessageDto): ForwardItem | null {
  const body = m.body ?? '';
  switch (m.contentType) {
    case 'sticker':
      return body ? { kind: 'sticker', sticker: body } : null;
    case 'location':
      return m.location ? { kind: 'location', point: { lat: m.location.lat, lng: m.location.lng, accuracy: m.location.accuracy ?? null } } : null;
    case 'contact':
      return m.contact ? { kind: 'contact', card: m.contact } : null;
    case 'poll':
      return m.poll ? { kind: 'poll', poll: { question: m.poll.question, options: m.poll.options.map((o) => o.text), multiple: m.poll.multiple, anonymous: m.poll.anonymous } } : null;
    case 'event': {
      const e = m.event;
      // A called-off event is not passed on: the copy would look like it is still on.
      if (!e || e.cancelled) return null;
      return { kind: 'event', event: { title: e.title, startsAt: e.startsAt, endsAt: e.endsAt, location: e.location, description: e.description } };
    }
  }
  const a = m.attachment;
  if (a) {
    return {
      kind: 'file',
      source: { encrypted: false, attachmentId: a.id },
      meta: {
        name: a.name,
        // The server files a recorded voice note by its container (WebM → video/webm); as audio it plays as one.
        mime: a.kind === 'voice' ? a.mime.replace(/^video\//, 'audio/') : a.mime,
        size: a.size,
        voice: a.kind === 'voice',
        ...(a.durationMs ? { durationMs: a.durationMs } : {}),
        ...(a.waveform?.length ? { waveform: a.waveform } : {}),
        ...(a.round ? { round: true } : {}),
        ...(a.kind === 'file' ? { document: true } : {}),
        ...(a.sticker ? { sticker: true } : {}),
        ...(a.width && a.height ? { width: a.width, height: a.height } : {}),
      },
      caption: body,
    };
  }
  return body.trim() ? { kind: 'text', body, linkCard: null, linkPreview: m.linkPreview } : null;
}

/** An encrypted message, decrypted on this device. */
function decryptedItem(c: DecryptedContent): ForwardItem | null {
  const body = text(c.body);
  switch (c.msgtype) {
    case 'm.text':
    case undefined:
      return body.trim() ? { kind: 'text', body, linkCard: readLinkCard((c as { link_preview?: unknown }).link_preview), linkPreview: null } : null;
    case 'm.sticker':
      return body ? { kind: 'sticker', sticker: body } : null;
    case 'm.location': {
      const point = geoPoint(c.geo_uri);
      return point ? { kind: 'location', point } : null;
    }
    case CONTACT:
      return isCard(c.contact) ? { kind: 'contact', card: { id: c.contact.id, username: c.contact.username, displayName: c.contact.displayName } } : null;
    case POLL:
      return c.poll && typeof c.poll.question === 'string' && Array.isArray(c.poll.options)
        ? { kind: 'poll', poll: { question: c.poll.question, options: c.poll.options.map(String), multiple: Boolean(c.poll.multiple), anonymous: Boolean(c.poll.anonymous) } }
        : null;
    case EVENT:
      return c.event && typeof c.event.title === 'string' && typeof c.event.startsAt === 'string'
        ? { kind: 'event', event: { title: c.event.title, startsAt: c.event.startsAt, endsAt: c.event.endsAt ?? null, location: c.event.location ?? null, description: c.event.description ?? null } }
        : null;
    case 'm.image':
    case 'm.video':
    case 'm.audio':
    case 'm.file': {
      const url = c.file?.url;
      const id = typeof url === 'string' && url.startsWith('mxc://dijitalapp.com/') ? url.slice('mxc://dijitalapp.com/'.length) : null;
      if (!id || !c.file) return null;
      const info = Object.fromEntries(Object.entries(c.file).filter(([k]) => k !== 'url'));
      const i = c.info ?? {};
      const width = num(i.w);
      const height = num(i.h);
      return {
        kind: 'file',
        source: { encrypted: true, attachmentId: id, info },
        meta: {
          name: body || 'file',
          mime: text(i.mimetype) || 'application/octet-stream',
          size: num(i.size) ?? 0,
          // Inside encryption a voice note is audio with a length (an attached song has none).
          voice: c.msgtype === 'm.audio' && Boolean(num(i.duration)),
          ...(num(i.duration) ? { durationMs: num(i.duration) } : {}),
          ...(Array.isArray(i.waveform) && i.waveform.length ? { waveform: i.waveform.filter((x) => typeof x === 'number') } : {}),
          ...(i.round ? { round: true } : {}),
          ...(c.msgtype === 'm.file' ? { document: true } : {}),
          ...(i.sticker ? { sticker: true } : {}),
          ...(typeof i.thumbnail_data === 'string' && i.thumbnail_data ? { thumb: i.thumbnail_data } : {}),
          ...(width && height ? { width, height } : {}),
        },
        caption: text(c.caption),
      };
    }
  }
  return null;
}

/**
 * What would be forwarded: from the message itself, or from its decrypted content for an encrypted one. null:
 * nothing to forward (view once, deleted, a notice, or content this app does not know).
 */
export function forwardItem(m: MessageDto, decrypted: DecryptedContent | null): ForwardItem | null {
  if (m.viewOnce || m.deleted || m.contentType === 'system') return null;
  if (m.contentType === 'encrypted') return decrypted ? decryptedItem(decrypted) : null;
  return plainItem(m);
}

/** Downloads a file, telling how far it got (0–1) when the size is known. */
export async function fetchBytes(url: string, onProgress?: (share: number) => void): Promise<Uint8Array> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'same-origin' });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (!res.ok) throw new ApiError(res.status === 404 ? 'NOT_FOUND' : 'UNKNOWN', res.status);
  const total = Number(res.headers.get('content-length')) || 0;
  if (!res.body || !total || !onProgress) return new Uint8Array(await res.arrayBuffer());
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    got += value.length;
    onProgress(Math.min(1, got / total));
  }
  const bytes = new Uint8Array(got);
  let at = 0;
  for (const c of chunks) {
    bytes.set(c, at);
    at += c.length;
  }
  return bytes;
}

/** The code of a failed copy: what the server said, or what went wrong on this device. */
export function failureOf(err: unknown): ForwardFailure {
  if (err instanceof ApiError) return err.code;
  return 'UNKNOWN';
}

/** A short reason for one chat in the forward / send result list ("You can't write here", "Blocked"…). */
export function failureKey(code: ForwardFailure): MessageKey {
  switch (code) {
    case 'FORBIDDEN':
    case 'POLICY_LOCKED':
      return 'forward.why.forbidden';
    case 'MESSAGING_NOT_ALLOWED':
      return 'forward.why.notAllowed';
    case 'MESSAGE_REQUEST_PENDING':
      return 'forward.why.request';
    case 'MESSAGE_TOO_LARGE':
    case 'STORAGE_QUOTA_EXCEEDED':
      return code === 'STORAGE_QUOTA_EXCEEDED' ? 'forward.why.storage' : 'forward.why.tooLarge';
    case 'ATTACHMENT_REJECTED':
    case 'ATTACHMENT_MALWARE':
      return 'forward.why.file';
    case 'SLOW_MODE':
    case 'RATE_LIMITED':
      return 'forward.why.slow';
    case 'NETWORK':
      return 'forward.why.offline';
    case 'E2EE_UNAVAILABLE':
    case 'E2EE_SEND_FAILED':
    case 'E2EE_SIGNING_KEY_MISSING':
      return 'forward.why.keys';
    case 'E2EE_DEVICE_UNVERIFIED':
      return 'forward.why.unverified';
    case 'E2EE_IDENTITY_CHANGED':
      return 'forward.why.identity';
    case 'UNREADABLE':
      return 'forward.why.unreadable';
    case 'NOT_FORWARDABLE':
    case 'E2EE_UNSUPPORTED':
      return 'forward.why.notForwardable';
    case 'FEATURE_NOT_ENABLED':
      return 'forward.why.feature';
    case 'NOT_FOUND':
      return 'forward.why.gone';
    case 'CHAT_LOCKED':
      return 'forward.why.locked';
    default:
      return 'forward.why.unknown';
  }
}
